import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api-context.tsx'
import { eventKeys } from './useEvents.ts'

/**
 * Getting an event signed off.
 *
 * Every mutation invalidates the whole events tree, not just the approval it
 * touched: a decision changes the event's `status`, which changes whether it
 * appears in the calendar at all. Invalidating only the approval query would
 * leave an approved event missing from the month grid until a reload.
 */

export const approvalKeys = {
  pending: () => [...eventKeys.all(), 'approvals', 'pending'] as const,
  history: (eventId: string) => [...eventKeys.all(), 'approvals', eventId] as const,
  conflicts: (eventId: string) => [...eventKeys.all(), 'conflicts', eventId] as const,
}

/** The approver inbox. 403 for anyone without `events:admin`, so gate the call. */
export function usePendingApprovals(enabled = true) {
  const api = useApi()
  return useQuery({
    queryKey: approvalKeys.pending(),
    enabled,
    queryFn: async () => {
      const { data, error } = await api.api.v1.events.approvals.get()
      if (error) throw error
      return data
    },
  })
}

export type EventApproval = NonNullable<ReturnType<typeof usePendingApprovals>['data']>[number]

export function useApprovalHistory(eventId: string, enabled = true) {
  const api = useApi()
  return useQuery({
    queryKey: approvalKeys.history(eventId),
    enabled: enabled && eventId !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.events({ id: eventId }).approvals.get()
      if (error) throw error
      return data
    },
  })
}

/**
 * What an event's coming dates clash with — another event in the same place
 * at the same time, or a room of that name held for something else. Advice
 * for whoever sends it for approval or approves it; `events:write` to ask.
 */
export function useEventConflicts(eventId: string, enabled = true) {
  const api = useApi()
  return useQuery({
    queryKey: approvalKeys.conflicts(eventId),
    enabled: enabled && eventId !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.events({ id: eventId }).conflicts.get()
      if (error) throw error
      return data
    },
  })
}

export type EventConflict = NonNullable<ReturnType<typeof useEventConflicts>['data']>[number]

function useApprovalInvalidation() {
  const queryClient = useQueryClient()
  // The event's status moves with the decision, so the calendar and the lists
  // are stale too — invalidate the tree rather than the one query.
  return () => void queryClient.invalidateQueries({ queryKey: eventKeys.all() })
}

export function useSubmitForApproval() {
  const api = useApi()
  const invalidate = useApprovalInvalidation()
  return useMutation({
    mutationFn: async ({ eventId, note }: { eventId: string; note?: string | null }) => {
      const { data, error } = await api.api.v1.events({ id: eventId }).approvals.post({ note })
      if (error) throw error
      return data
    },
    onSuccess: invalidate,
  })
}

/**
 * Approve, reject or withdraw.
 *
 * A rejection without a note is refused by the server with a 422 — a "no"
 * nobody explained is a "no" somebody has to come and ask about — so the form
 * requires one rather than letting the round trip do it.
 */
export function useDecideApproval() {
  const api = useApi()
  const invalidate = useApprovalInvalidation()
  return useMutation({
    mutationFn: async ({
      eventId,
      ...body
    }: {
      eventId: string
      action: 'approve' | 'reject' | 'withdraw'
      note?: string | null
    }) => {
      const { data, error } = await api.api.v1.events({ id: eventId }).approvals.decision.post(body)
      if (error) throw error
      return data
    },
    onSuccess: invalidate,
  })
}
